<script setup lang="ts">
import { computed } from "vue";

import theCard from "@/components/theCard.vue";
import theProductCard from "@/components/theProductCard.vue";
import { useCardBookmark } from "@/composables/useCardBookmark";
import { useCardTelemetry } from "@/composables/useCardTelemetry";
import { launchScormContent } from "@/services/scormService/launcher";
import type { ScormContent } from "@/services/scormService/types";
import { EventBus } from "@/store/eventBus";
import isUnauth from "@/utils/isUnauth";

const props = withDefaults(
  defineProps<{
    result: any;
    //eslint-disable-next-line
    index?: number;
    status?: any;
    forCarousel?: boolean;
    cardView?: string;
    cardStyle?: string;
    priceView?: string;
    topicView?: boolean;
    isStoreContent?: boolean;
    showDescription?: boolean;
  }>(),
  {
    index: 0,
    status: null,
    forCarousel: false,
    cardView: "Grid",
    cardStyle: "Grid",
    priceView: "USD",
    topicView: false,
    isStoreContent: false,
    showDescription: true,
  }
);

// Composables
const { bookmarkMe, debouncedBookmarkMe } = useCardBookmark();

const { sendTelemetry: sendTelemetryBase } = useCardTelemetry({
  skipNavigation: true,
  onSuccess: async () => {
    const scormContent = props.result as unknown as ScormContent;

    // Launch SCORM content with full session management
    const result = await launchScormContent(scormContent, {
      openInNewWindow: true,
      onLaunchSuccess: (attemptId, launchUrl) => {
        console.log("SCORM launch successful:", { attemptId, launchUrl });
        EventBus.emit("showToast", {
          message: "SCORM content launched successfully!",
          type: "SUCCESS",
        });
      },
      onLaunchError: (error) => {
        console.error("SCORM launch error:", error);
        EventBus.emit("showToast", {
          message: `SCORM launch failed: ${error.message}`,
          type: "ERROR",
        });
      },
    });

    if (!result.success) {
      console.error("SCORM launch failed:", result.error);
      EventBus.emit("showToast", {
        message:
          result.error?.message || "SCORM launch failed. Please try again.",
        type: "ERROR",
      });
    }
  },
  onError: (error) => {
    console.error("Error during SCORM launch:", error);
    EventBus.emit("showToast", {
      message:
        "Failed to launch SCORM content. Please check your connection and try again.",
      type: "ERROR",
    });
  },
});

// Computed properties
const resultLink = computed(() => {
  // SCORM content launches in a new window, no detail page route needed
  return "";
});

// Methods
const sendTelemetry = async () => {
  await sendTelemetryBase(props, resultLink.value);
};

defineExpose({
  bookmarkMe,
  debouncedBookmarkMe,
  sendTelemetry,
  resultLink,
});
</script>

<template>
  <div
    :class="[
      'relative z-10 h-full hover:z-[700] focus:z-[700] active:z-[700]',
      {
        'w-[19rem]': props.cardView === 'Grid' && !topicView,
        'w-full': props.cardView !== 'Grid' && !topicView,
        'w-[22rem]': topicView,
      },
    ]"
    data-cy="scorm-card"
    v-show="result"
  >
    <the-card
      v-if="!isStoreContent"
      :is-unauth="isUnauth()"
      :for-carousel="forCarousel"
      :card-view="props.cardView"
      :card-style="props.cardStyle"
      :result="result"
      :status="status"
      :cy="'scorm-card-link'"
      :link="resultLink"
      :show-description="props.showDescription"
      @navigate="sendTelemetry"
      @bookmark="debouncedBookmarkMe(result)"
    />
    <the-product-card
      v-else
      :is-unauth="isUnauth()"
      :card-view="props.cardView"
      :card-style="props.cardStyle"
      :price-view="priceView"
      :result="result"
      :status="status"
      :cy="'scorm-product-card'"
      :link="resultLink"
      :show-description="props.showDescription"
      @navigate="sendTelemetry"
      @bookmark="debouncedBookmarkMe(result)"
    />
  </div>
</template>
